import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  message,
  Upload,
  Select,
  Button,
  Space,
  Alert,
  Row,
  Col,
} from "antd";
import { API_METHODS } from "../../../constants";
import { UploadOutlined } from "@ant-design/icons";
import { getClientConfig, isEmpty } from "../../../generalFunctions";
import useApi from "../../../hooks/useApi";
import TableComponent from "../../common/table/TableComponent";

function BulkUploadBeneficiary(props) {
  const { visible, close } = props;
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResponse, setUploadResponse] = useState(null);
  const [filteredProjects, setFilteredProjects] = useState([]);

  const { company, project, ward } = getClientConfig();
  const [uploadResponseData, uploadRequest] = useApi(
    `/config/user/bulk-create`,
    null,
    API_METHODS.POST,
    true
  );

  // File validation
  const uploadProps = {
    beforeUpload: (file) => {
      const isExcel =
        file.type === "application/vnd.ms-excel" ||
        file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      if (!isExcel) {
        message.error(
          `${file.name} error. Only Excel files (.xlsx, .xls) supported`
        );
      }
      return isExcel || Upload.LIST_IGNORE;
    },
  };

  const handleFileChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  const handleCompanyChange = (companyId) => {
    if (!isEmpty(project)) {
      const filtered = project.filter((p) => p.companyId === companyId);
      setFilteredProjects(filtered);
      form.setFieldValue("projectId", undefined);
    }
  };

  const handleUpload = () => {
    form
      .validateFields()
      .then((values) => {
        if (fileList.length === 0) {
          message.error("Please select an Excel file");
          return;
        }

        setUploading(true);

        const formData = new FormData();
        formData.append("beneficiaries", fileList[0].originFileObj);
        formData.append("projectId", values.projectId);
        formData.append("wardId", values.wardId);
        formData.append("companyId", values.companyId);

        uploadRequest(formData);
      })
      .catch((e) => {
        console.error("Form validation failed:", e);
      });
  };

  useEffect(() => {
    if (uploadResponseData.data && !uploadResponseData.isLoading) {
      setUploading(false);

      if (
        uploadResponseData.data.successCount &&
        uploadResponseData.data.successCount > 0
      ) {
        // Success case
        message.success(
          `${uploadResponseData.data.successCount} beneficiary users created successfully`
        );
        setTimeout(() => {
          close(true);
        }, 2000);
      } else if (
        uploadResponseData.data &&
        Array.isArray(uploadResponseData.data)
      ) {
        // Error case - array of error objects
        setUploadResponse({
          errorRecords: uploadResponseData.data,
          isError: true,
        });
      } else if (
        uploadResponseData.data &&
        uploadResponseData.data.errorRecords
      ) {
        // Error case - object with errorRecords
        setUploadResponse({
          errorRecords: uploadResponseData.data.errorRecords,
          isError: true,
        });
      }
    } else if (uploadResponseData.error) {
      setUploading(false);
      message.error("Upload failed. Please try again.");
    }
  }, [uploadResponseData]);

  const handleCancel = () => {
    form.resetFields();
    setFileList([]);
    setUploadResponse(null);
    setUploading(false);
    setFilteredProjects([]);
    close();
  };

  const dummyRequest = ({ file, onSuccess }) => {
    setTimeout(() => {
      onSuccess("ok");
    }, 0);
  };

  // Error table columns
  const errorColumns = [
    {
      title: "Row Number",
      dataIndex: "rowNumber",
      width: 100,
    },
    {
      title: "Username",
      dataIndex: "username",
      width: 150,
    },
    {
      title: "Error Message",
      dataIndex: "errors",
    },
  ];

  // Determine modal footer buttons
  const getModalFooter = () => {
    if (uploadResponse && uploadResponse.isError) {
      return [
        <Button key="close" onClick={handleCancel}>
          Close
        </Button>,
      ];
    }

    return [
      <Button key="cancel" onClick={handleCancel}>
        Cancel
      </Button>,
      <Button
        key="upload"
        type="primary"
        onClick={handleUpload}
        loading={uploading}
        disabled={
          fileList.length === 0 ||
          !form.getFieldValue("companyId") ||
          !form.getFieldValue("projectId") ||
          !form.getFieldValue("wardId")
        }
      >
        Upload
      </Button>,
    ];
  };

  return (
    <Modal
      title="Bulk Upload Beneficiary Users"
      open={visible}
      onCancel={handleCancel}
      width={900}
      footer={getModalFooter()}
      destroyOnClose={true}
    >
      {!uploadResponse ? (
        <Form layout="vertical" form={form}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="companyId"
                label="Company"
                rules={[{ required: true, message: "Company is required" }]}
              >
                <Select
                  placeholder="Select Company"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  onSelect={handleCompanyChange}
                >
                  {!isEmpty(company) &&
                    company.map((option) => (
                      <Select.Option key={option.id} value={option.id}>
                        {option.companyName}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="projectId"
                label="Project"
                rules={[{ required: true, message: "Project is required" }]}
              >
                <Select
                  placeholder="Select Project"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {!isEmpty(filteredProjects) &&
                    filteredProjects.map((option) => (
                      <Select.Option key={option.id} value={option.id}>
                        {option.name}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="wardId"
                label="Ward"
                rules={[{ required: true, message: "Ward is required" }]}
              >
                <Select
                  placeholder="Select Ward"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {!isEmpty(ward) &&
                    ward.map((option) => (
                      <Select.Option key={option.id} value={option.id}>
                        {option.name}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="Upload Excel File">
                <Upload
                  {...uploadProps}
                  maxCount={1}
                  accept=".xlsx,.xls"
                  fileList={fileList}
                  onChange={handleFileChange}
                  disabled={uploading}
                  customRequest={dummyRequest}
                >
                  <Button icon={<UploadOutlined />} disabled={uploading}>
                    {fileList.length > 0
                      ? fileList[0].name
                      : "Click to select file"}
                  </Button>
                </Upload>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      ) : (
        <Space direction="vertical" style={{ width: "100%" }} size="large">
          <Alert
            message={`Found ${uploadResponse.errorRecords.length} error(s)`}
            description="Please fix the errors below and upload again."
            type="error"
            showIcon
          />

          <TableComponent
            columns={errorColumns}
            tableData={uploadResponse.errorRecords}
            loading={uploading}
            clientSideRender={true}
          />
        </Space>
      )}
    </Modal>
  );
}

export default BulkUploadBeneficiary;
